import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "../button/wow-button";
import "../form-elements/textarea/textarea";

import { faqBlockStyles } from "./faq-block.css";

@customElement("faq-block")
export class FaqBlock extends LitElement {
  static override styles = [faqBlockStyles];

  @property({ type: Number }) blockNumber!: number;
  @property() question = "";
  @property() answer = "";
  @property() questionPlaceholder = "";
  @property() answerPlaceholder = "";
  @property() blockId!: string;

  @state() private isRemoving = false;

  private handleQuestionChange = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.dispatchEvent(
      new CustomEvent("question-change", {
        bubbles: true,
        composed: true,
        detail: {
          blockId: this.blockId,
          value: target.value,
        },
      }),
    );
  };

  private handleAnswerChange = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.dispatchEvent(
      new CustomEvent("answer-change", {
        bubbles: true,
        composed: true,
        detail: {
          blockId: this.blockId,
          value: target.value,
        },
      }),
    );
  };

  private handleDeleteClick = async () => {
    this.isRemoving = true;

    // Get the FAQ block element within this component's shadow DOM
    const blockElement = this.shadowRoot?.querySelector(".faq-block");
    if (blockElement) {
      // Wait for the CSS transition to complete
      await new Promise((resolve) => {
        const handleTransitionEnd = () => {
          blockElement.removeEventListener("transitionend", handleTransitionEnd);
          resolve(undefined);
        };
        blockElement.addEventListener("transitionend", handleTransitionEnd);

        // Fallback timeout in case transitionend doesn't fire
        setTimeout(resolve, 500);
      });
    }

    // Now dispatch the delete event after animation completes
    this.dispatchEvent(
      new CustomEvent("delete-requested", {
        bubbles: true,
        composed: true,
        detail: {
          blockId: this.blockId,
        },
      }),
    );
  };

  protected override render() {
    return html`
      <div class=${classMap({ "faq-block": true, removing: this.isRemoving })}>
        <div class="faq-block-header">
          <span class="faq-block-number">FAQ ${this.blockNumber}</span>
          <wow-button variant="neutral" size="small" @click=${this.handleDeleteClick}>
            <ph-trash-simple size="16"></ph-trash-simple>
          </wow-button>
        </div>

        <div class="form-field">
          <wow-textarea
            .value=${this.question}
            @input=${this.handleQuestionChange}
            maxlength="250"
            rows="2"
            placeholder=${this.questionPlaceholder}
          ></wow-textarea>
        </div>

        <div class="form-field">
          <wow-textarea
            .value=${this.answer}
            @input=${this.handleAnswerChange}
            maxlength="750"
            rows="4"
            placeholder=${this.answerPlaceholder}
          ></wow-textarea>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "faq-block": FaqBlock;
  }
}
